import axios from 'axios';
import { requestIdHeaders } from './requestId';

/**
 * Access-token refresh shared by the HTTP layer and the WebSocket (ZLAYT-1447).
 *
 * The backend rotates refresh tokens and blacklists the old one, so two
 * refreshes started with the same refresh token cannot both succeed: the
 * loser gets a 401 and the HTTP layer signs the user out. Every caller
 * therefore shares one in-flight request.
 */

let inFlight: Promise<string> | null = null;

/** Tokens are stored JSON-encoded; "null" means signed out. */
const readStoredToken = (key: string): string | null => {
  const raw = localStorage.getItem(key);
  if (!raw || raw === 'null') return null;
  try {
    const value = JSON.parse(raw);
    return typeof value === 'string' ? value : null;
  } catch {
    return raw;
  }
};

export const getStoredAccessToken = () => readStoredToken('accessToken');

/** The exp claim of a JWT in seconds, or null when it cannot be read. */
const getJwtExp = (token: string): number | null => {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const exp = JSON.parse(atob(padded))?.exp;
    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
};

/**
 * True when the token has expired or will within skewSeconds. A token that
 * cannot be read counts as not expiring: the server decides, and a 4401
 * close from it forces a refresh anyway.
 */
export const isAccessTokenExpiring = (
  token: string,
  skewSeconds = 60,
  now = Date.now()
): boolean => {
  const exp = getJwtExp(token);
  if (exp === null) return false;
  return exp * 1000 - skewSeconds * 1000 <= now;
};

/** Exchange the stored refresh token for a new pair; concurrent calls share one request. */
export const refreshAccessToken = (
  baseURL: string = import.meta.env.VITE_APP_API_URL
): Promise<string> => {
  if (!inFlight) {
    inFlight = (async () => {
      const refresh = readStoredToken('refreshToken');
      if (!refresh) throw new Error('No refresh token');
      const response = await axios.post(
        `${baseURL}/user-auth/refresh/`,
        { refresh },
        // Plain axios has no interceptors: send the id here (ZLAYT-1519).
        { headers: requestIdHeaders() }
      );
      const { access, refresh: rotated } = response.data;
      localStorage.setItem('accessToken', JSON.stringify(access));
      localStorage.setItem('refreshToken', JSON.stringify(rotated ?? refresh));
      return access as string;
    })().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
};
